import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { TbBasket, TbHeart } from "react-icons/tb";
import { Link } from "react-router-dom";
import ProductSke from "../Skeletons/ProductSke";
import { displayImage } from "../../helpers/config";
import FormatCurrency from "../../helpers/FormatCurrency";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LinkButton from "../../ui/LinkButton";
import Button from "../../ui/Button";
import { apiClient } from "../../helpers/Axios";
import toast from "react-hot-toast";
import SmallLinkButton from "../../ui/SmallLinkButton";
import SmallButton from "../../ui/SmallButton";
import { useConfig } from "../../context/ConfigContext";
import { finalPrice } from "../../helpers/finalPrice";

function Product({ data, loading }) {
  const [isHover, setIsHover] = useState(false);

  const images = JSON.parse(data?.images || "[]");
  const [imageLoading, setImageLoading] = useState(true);

  const [wishlists, setWishlists] = useState([]);

  // Retrieve wishlist from localStorage on component mount
  useEffect(() => {
    const savedWishlist = JSON.parse(localStorage.getItem("wishlists")) || [];
    setWishlists(savedWishlist);
  }, []);

  // Add to wishlist

  const queryClients = useQueryClient();

  const addToWishlist = (slug) => {
    const isThere = wishlists?.some((w) => w === slug);

    if (isThere) {
      const filtered = wishlists?.filter((w) => w !== slug);
      setWishlists(filtered);
      localStorage.setItem("wishlists", JSON.stringify(filtered));
    } else {
      const nf = [...wishlists, slug];
      setWishlists(nf);
      localStorage.setItem("wishlists", JSON.stringify(nf));
    }
  };

  // Add to cart

  const { setIsMiniCartOpen } = useConfig();

  const { mutate, isPending } = useMutation({
    mutationFn: async (formData) => {
      return apiClient.post("/cart/add", formData);
    },
    onSuccess: () => {
      queryClients.invalidateQueries({
        predicate: (q) => q.queryKey.includes("cart"),
      });
      setIsMiniCartOpen(true);
    },
    onError: (e) => {
      toast.dismiss();
      toast.error(e?.response?.data || "Error whiles adding to cart");
    },
  });

  const handleAddToCart = () => {
    if (!data?.in_stock) {
      return;
    }

    const v = null;
    const formData = {
      quantity: 1,
      product: data?.id,
      variant: v,
      customise: data?.customable
        ? window.confirm("Do you want customisation")
        : false,
    };

    mutate(formData);
  };

  if (loading) {
    return (
      <>
        <ProductSke />
      </>
    );
  }

  return (
    <div className="w-full relative">
      <div className="space-y-2 absolute left-3 top-3 z-10">
        {!data?.in_stock ? (
          <div className="bg-secondary text-xs text-white px-2 py-1 rounded-full">
            Out of stock
          </div>
        ) : null}

        {data?.discount ? (
          <div
            className={`text-[13px] bg-secondary text-white px-4 py-1 rounded-full w-fit`}
          >
            -{data?.discount}%
          </div>
        ) : null}
      </div>

      <div className="w-full">
        {/* Product Image and others */}
        <motion.div
          className="w-full relative overflow-hidden"
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          {/* Image */}
          <div className="relative">
            <Link to={`/product/${data?.slug}`}>
              <div className="overflow-hidden w-full cursor-pointer relative pt-[130%] rounded-xl aspect-[3/4]">
                <AnimatePresence>
                  {imageLoading && (
                    <motion.div className="absolute z-50 w-full h-full top-0 left-0 right-0 bottom-0 animate-pulse bg-gradient-to-r from-gray-50 to-white" />
                  )}
                </AnimatePresence>
                <motion.img
                  src={displayImage(images[0])}
                  onLoad={() => setImageLoading(false)}
                  loading="lazy"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.2 }}
                  alt=""
                  className="w-full h-full object-cover absolute  
              top-0 bottom-0 right-0 left-0 bg-white border-none"
                />
              </div>
            </Link>

            {/* Cart Actions for Large Screens */}
            <div className="hidden lg:block">
              <motion.div
                initial={{ y: 100 }}
                transition={{ duration: 0.2 }}
                animate={{ y: isHover ? 0 : 100 }}
                className="px-5 absolute bottom-5 w-full"
              >
                {data?.variants && data?.variants?.length > 0 ? (
                  <LinkButton to={`/product/${data?.slug}`} fullWidth>
                    Add to cart
                  </LinkButton>
                ) : (
                  <Button
                    white
                    loading={isPending}
                    onClick={handleAddToCart}
                    fullWidth
                  >
                    {!data?.in_stock ? "Out of stock" : "ADD TO CART"}
                  </Button>
                )}
              </motion.div>

              {/*  Favourite*/}
              <motion.div
                initial={{ x: 500 }}
                transition={{ duration: 0.2 }}
                animate={{ x: isHover ? 0 : 100 }}
                className="absolute z-50 right-4 top-3"
              >
                <button
                  onClick={() => addToWishlist(data?.slug)}
                  className={`size-11 rounded-full shadow-lg
                  ${wishlists?.some((w) => w === data?.slug) ? "bg-black text-white" : "bg-white text-black"}
                flex justify-center items-center hover:bg-black hover:text-white`}
                >
                  <TbHeart />
                </button>
              </motion.div>
            </div>
          </div>

          {/* Cart Actions for Small Screen */}
          <div className="absolute bottom-5 right-5 flex flex-col items-center gap-2 lg:hidden">
            {data?.variants && data?.variants?.length > 0 ? (
              <SmallLinkButton to={`/product/${data?.slug}`}>
                <TbBasket />
              </SmallLinkButton>
            ) : (
              <SmallButton loading={isPending} onClick={handleAddToCart}>
                <TbBasket />
              </SmallButton>
            )}

            <button
              onClick={() => addToWishlist(data?.slug)}
              className={`size-11 rounded-full shadow-lg
                  ${wishlists?.some((w) => w === data?.slug) ? "bg-black text-white" : "bg-white text-black"}
                flex justify-center items-center hover:bg-black hover:text-white`}
            >
              <TbHeart />
            </button>
          </div>
        </motion.div>

        {/* Product Details */}
        <div className="w-full mt-4">
          <Link to={`/product/${data?.slug}`}>
            <h1 className=" truncate text-[clamp(0.9rem,2vw+1rem,1.08rem)]">
              {data?.name}
            </h1>
          </Link>
          <div className="flex gap-2 items-center">
            <h2
              className={`text-[clamp(0.7rem,2vw+1rem,0.9rem)] font-medium ${data?.discount && "text-secondary"}`}
            >
              <FormatCurrency
                amount={finalPrice(data?.base_price, data?.discount)}
              />
            </h2>
            {data?.discount ? (
              <h2
                className={`text-[clamp(0.7rem,2vw+1rem,0.9rem)] text-gray-600 line-through`}
              >
                <FormatCurrency amount={data?.base_price} />
              </h2>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Product;
